import chalk from "chalk";
import type { CaiConfig, DriftCheckerName, DriftIssue, IssueCode } from "./types.js";
import { runDriftCheck } from "./drift/index.js";
import { ISSUE_CODES, specFor } from "./drift/issue-codes.js";
import type { FixActionName, FixContext, FixResult } from "./drift/issue-codes.js";

export interface FixActionResult {
  action: FixActionName;
  changed: string[];
}

export interface FixRunResult {
  applied: FixActionResult[];
  skipped: FixActionResult[];
  initialIssueCount: number;
  remainingIssueCount: number;
}

export async function runAutoFix(
  config: CaiConfig,
  opts: { dryRun?: boolean; only?: DriftCheckerName[]; skip?: DriftCheckerName[] } = {}
): Promise<FixRunResult> {
  const initialReport = await runDriftCheck(config, {
    only: opts.only,
    skip: opts.skip,
  });

  const fixCtx: FixContext = { config, issues: initialReport.issues };
  const candidates = collectFixCandidates(fixCtx);

  const applied: FixActionResult[] = [];
  const skipped: FixActionResult[] = [];

  for (const code of candidates) {
    const spec = specFor(code);
    if (!spec.fix || !spec.action) continue;
    const preview = spec.fix.preview(fixCtx);
    if (preview.length === 0) continue;

    if (opts.dryRun) {
      skipped.push({ action: spec.action, changed: [...preview] });
      continue;
    }

    const result = await spec.fix.apply(fixCtx);
    applied.push({ action: spec.action, changed: [...result.changed] });
  }

  const finalReport = (opts.dryRun || candidates.length === 0)
    ? initialReport
    : await runDriftCheck(config, {
        only: opts.only,
        skip: opts.skip,
      });

  return {
    applied,
    skipped,
    initialIssueCount: initialReport.issues.length,
    remainingIssueCount: finalReport.issues.length,
  };
}

const ACTION_LABELS: Record<string, string> = Object.fromEntries(
  (Object.values(ISSUE_CODES) as Array<{ action?: string; label: string }>)
    .filter((spec) => spec.action !== undefined)
    .map((spec) => [spec.action!, spec.label])
);

export function printFixResult(result: FixRunResult, opts: { dryRun?: boolean } = {}): void {
  const b   = chalk.bold;
  const dim = chalk.dim;

  console.log();
  if (opts.dryRun) {
    console.log(`  ${b("cai fix")} ${dim("· dry run — no changes written")}`);
    console.log();
    if (result.skipped.length === 0) {
      console.log(`  ${chalk.cyan("ℹ")} ${dim("No auto-fixable scaffold drift found.")}`);
      console.log(`     ${dim("Run ")}${chalk.white("cai sync")}${dim(" to let AI update the remaining docs.")}`);
    } else {
      for (const action of result.skipped) {
        const label = ACTION_LABELS[action.action] ?? action.action;
        const files = action.changed.length > 0 ? chalk.cyan(action.changed.join(", ")) : dim("(no file changes)");
        console.log(`  ${chalk.yellow("⚠")} ${label} — ${files}`);
      }
    }
    console.log();
    return;
  }

  console.log(`  ${b("cai fix")} ${dim("· updating scaffold docs")}`);
  console.log();

  if (result.applied.length === 0) {
    console.log(`  ${chalk.cyan("ℹ")} ${dim("No auto-fixable scaffold drift found.")}`);
    console.log();
    console.log(`     ${dim("Run ")}${chalk.white("cai sync")}${dim(" to let AI fix the rest.")}`);
  } else {
    for (const action of result.applied) {
      const label = ACTION_LABELS[action.action] ?? action.action;
      const files = action.changed.length > 0 ? `  ${dim("→")} ${chalk.cyan(action.changed.join(", "))}` : "";
      console.log(`  ${chalk.green("✔")} ${label}${files}`);
    }
    console.log();
    const fixed = result.initialIssueCount - result.remainingIssueCount;
    if (result.remainingIssueCount > 0) {
      console.log(dim(`  ${fixed} issue${fixed !== 1 ? "s" : ""} resolved · ${result.remainingIssueCount} remaining`));
      console.log(`  ${chalk.cyan("ℹ")} ${dim("Run ")}${chalk.white("cai sync")}${dim(" to let AI fix the rest.")}`);
    } else {
      console.log(`  ${chalk.green("✔")} ${chalk.green("All issues resolved.")}`);
    }
  }
  console.log();
}

/** Collect the set of IssueCodes that have fixes available for this report. */
function collectFixCandidates(ctx: FixContext): readonly IssueCode[] {
  const seen = new Set<IssueCode>();
  const available: IssueCode[] = [];
  for (const issue of ctx.issues) {
    if (seen.has(issue.code)) continue;
    seen.add(issue.code);
    const spec = specFor(issue.code);
    if (!spec.fix) continue;
    if (spec.fix.preview(ctx).length === 0) continue;
    available.push(issue.code);
  }
  return available;
}